import { AUTHORS } from "../data/authors.js";

const SITE_URL = "https://talesandlegends.org";

/** The organiser/publisher block every other type on the site points at —
 * one object rather than five near-identical literals that can drift
 * apart. `legalName` carries the full registered form; `name` stays the
 * short one people actually search for. */
export const ORGANIZATION_REF = {
  "@type": "Organization",
  name: "Tales&Legends gUG",
  legalName: "Tales&Legends gemeinnützige UG (haftungsbeschränkt)",
  url: SITE_URL,
  logo: { "@type": "ImageObject", url: `${SITE_URL}/assets/brand/logo-mark.png` },
};

/**
 * "2026-03-14" + "18:00" -> "2026-03-14T18:00:00+01:00".
 *
 * The offset is derived from the date itself rather than hardcoded:
 * Germany is +01:00 in March and +02:00 in August, and an event dated on
 * the wrong side of the DST switch would otherwise be published an hour
 * out. Returns the plain date unchanged when no time is on file — a
 * date-only `startDate` is valid schema.org, a wrong timestamp is not.
 */
export function toIsoWithBerlinOffset(date, time) {
  if (!date || !time) return date;
  const offset = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin",
    timeZoneName: "longOffset",
  })
    .formatToParts(new Date(`${date}T12:00:00Z`))
    .find((part) => part.type === "timeZoneName")
    .value.replace("GMT", "");
  return `${date}T${time}:00${offset || "+00:00"}`;
}

/**
 * schema.org's `typicalAgeRange` is an open-ended string range: "6-12",
 * "16-" for a lower bound only, "-12" for an upper one. Returns undefined
 * when neither bound is on file, so the property is omitted rather than
 * emitted empty.
 */
export function typicalAgeRange({ ageMin, ageMax }) {
  if (ageMin == null && ageMax == null) return undefined;
  return `${ageMin ?? ""}-${ageMax ?? ""}`;
}

function isSingleDayWithUnknownEnd(frontmatter) {
  const endDate = frontmatter.endDate || frontmatter.date;
  return endDate === frontmatter.date && Boolean(frontmatter.startTime) && !frontmatter.endTime;
}

export function buildEventJsonLd(frontmatter, lang, { url } = {}) {
  // Past events used to drop `offers` entirely: a SoldOut offer on an event
  // that had simply already happened made the crawler flag micro-markup
  // errors (§ T-15). Search Console then reported the opposite — `offers`
  // missing on both events — so the encoding now says *when* the offer
  // stopped applying instead of omitting it: SoldOut plus a validThrough of
  // the event's own start date. If the crawler objects again, this comment
  // is the history: the answer is a better-dated offer, not no offer.
  const isPast = frontmatter.status === "past";
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: frontmatter.title,
    ...(frontmatter.metaDescription && { description: frontmatter.metaDescription }),
    ...(url && { url }),
    organizer: ORGANIZATION_REF,
    // Who puts the event on. For a guest appearance the orga running the
    // camp is named in the frontmatter; for our own events that is us.
    performer: frontmatter.orgaName
      ? { "@type": "PerformingGroup", name: frontmatter.orgaName }
      : ORGANIZATION_REF,
    startDate: toIsoWithBerlinOffset(frontmatter.date, frontmatter.startTime),
    // A single-day event whose start carries a time but whose end doesn't
    // would otherwise publish "starts 18:00, ends 00:00 the same day" —
    // an end before the start. Omitting endDate is both valid and true:
    // we know when the evening began, not when it broke up.
    ...(isSingleDayWithUnknownEnd(frontmatter)
      ? {}
      : { endDate: toIsoWithBerlinOffset(frontmatter.endDate || frontmatter.date, frontmatter.endTime) }),
    // ISO-8601 in the frontmatter precisely so it can go out unchanged —
    // a human-readable "3 Stunden" here would be invalid schema.
    ...(frontmatter.duration && { duration: frontmatter.duration }),
    ...(typicalAgeRange(frontmatter) && { typicalAgeRange: typicalAgeRange(frontmatter) }),
    ...(frontmatter.capacity != null && { maximumAttendeeCapacity: frontmatter.capacity }),
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: frontmatter.cancelled
      ? "https://schema.org/EventCancelled"
      : "https://schema.org/EventScheduled",
    location: {
      "@type": "Place",
      name: frontmatter.location,
      address: frontmatter.address && {
        "@type": "PostalAddress",
        streetAddress: frontmatter.address.street,
        postalCode: frontmatter.address.postalCode,
        addressLocality: frontmatter.address.locality,
        addressCountry: frontmatter.address.country || "DE",
      },
      ...(frontmatter.directionsUrl && { hasMap: frontmatter.directionsUrl }),
      geo: frontmatter.coordinates && {
        "@type": "GeoCoordinates",
        latitude: frontmatter.coordinates[0],
        longitude: frontmatter.coordinates[1],
      },
    },
    image: frontmatter.image && `${SITE_URL}${frontmatter.image}`,
    offers: buildEventOffers(frontmatter, { isPast }),
    inLanguage: lang,
  };
}

// One Offer per ticket type once frontmatter defines `tickets` (§ ticket
// types like SC/NPC/GSC/Solidarticket) — falls back to the single flat
// price/priceReduced offer for events that don't.
//
// `url` is only ever set when registration is actually open AND a real
// link is on file — never a leftover placeholder token, and never for a
// closed/sold-out event: once registration is closed the link has nothing
// left to invite someone to do, and offering it as "available" while
// linking a dead/never-filled-in registration form is exactly what
// leaked a literal "XXXXXXXX" token into a live Event's offers.url before.
function buildEventOffers(frontmatter, { isPast = false } = {}) {
  // `spotsLeft: 0` is a stronger statement than "registration is open":
  // an event can still be taking waitlist sign-ups while every seat is
  // gone. Zero seats wins.
  const soldOut = isPast || frontmatter.spotsLeft === 0 || !frontmatter.registrationOpen;
  const availability = soldOut ? "https://schema.org/SoldOut" : "https://schema.org/InStock";
  // For a past event the registration deadline is beside the point — what
  // makes the offer unambiguous is that it stopped applying when the event
  // began.
  const validThrough = isPast ? frontmatter.date : frontmatter.registrationDeadline;
  const url =
    frontmatter.registrationOpen && frontmatter.registrationUrl && !frontmatter.registrationUrl.includes("XXXXXXXX")
      ? frontmatter.registrationUrl
      : undefined;

  if (frontmatter.tickets?.length > 0) {
    return frontmatter.tickets.map((ticket) => ({
      "@type": "Offer",
      name: ticket.label,
      price: ticket.price,
      priceCurrency: "EUR",
      availability,
      ...(validThrough && { validThrough }),
      ...(url && { url }),
    }));
  }

  return {
    "@type": "Offer",
    price: frontmatter.price,
    priceCurrency: "EUR",
    availability,
    ...(validThrough && { validThrough }),
    ...(url && { url }),
  };
}

export function buildDefinedTermSetJsonLd(terms, name) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name,
    hasDefinedTerm: terms.map(({ term, definition }) => ({
      "@type": "DefinedTerm",
      name: term,
      description: definition,
    })),
  };
}

/** For LARP knowledge pages that are prose, not FAQ/glossary (§10.3), and
 * for donation project pages. `datePublished`/`dateModified`/`image`/
 * `author` are omitted entirely when not given, rather than emitting an
 * invalid/empty value — `image` and `author` are Google's two most-cited
 * recommended Article properties beyond what this already sets. */
export function buildArticleJsonLd({ title, url, lang, description, image, author, datePublished, dateModified }) {
  // A known author (§ src/data/authors.js) gets a real experience signal —
  // description + url + sameAs — instead of just a bare name, per Google's
  // E-E-A-T guidance for safety-adjacent content (weapons/safety, youth
  // protection). An unrecognized author string still gets a plain Person.
  const authorInfo = author && AUTHORS[author];
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    url,
    inLanguage: lang,
    description,
    ...(image && { image: image.startsWith("http") ? image : `${SITE_URL}${image}` }),
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    ...(url && { mainEntityOfPage: { "@type": "WebPage", "@id": url } }),
    author: {
      "@type": author ? "Person" : "Organization",
      name: author || "Tales&Legends gUG",
      ...(authorInfo && { description: authorInfo.bio[lang], url: authorInfo.url(lang), sameAs: authorInfo.sameAs }),
    },
    publisher: ORGANIZATION_REF,
  };
}

/** Site-level entity, emitted on the two homepages only. No SearchAction:
 * there is no search page for it to point at, and pointing one at a URL
 * that 404s is worse than omitting the property. */
export function buildWebSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Tales&Legends",
    url: SITE_URL,
    inLanguage: ["de", "en"],
    publisher: ORGANIZATION_REF,
  };
}

export function buildFaqJsonLd(qa) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qa.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}

/** Marks a hub/index page as an index rather than a standalone answer — the
 * one addition that reliably signals "this is a collection" to Google when
 * a hub's own intro copy inevitably brushes up against the same topic its
 * child pages answer in full. `items`: [{ name, url }] absolute URLs. */
export function buildCollectionPageJsonLd({ name, description, url, items }, lang) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url,
    inLanguage: lang,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        url: item.url,
      })),
    },
  };
}

/** items: [{ name, path }] — path relative, e.g. "/events" */
export function buildBreadcrumbJsonLd(items, lang) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}/${lang}${item.path}`,
    })),
  };
}
